/**
 * File System Access API Manager
 * Handles all interactions with the File System Access API
 */

// FileSystemManager is now decoupled from storage.
// Callers (UIManager) handle workspace persistence via storageManager.

/**
 * TypeScript declarations for FileSystemObserver (experimental API)
 * @see https://developer.chrome.com/blog/file-system-observer
 */

/**
 * Represents a file system change event detected by the FileSystemObserver
 */
export interface FileSystemChangeRecord {
  /** The original handle passed to observe() */
  root: FileSystemHandle;
  /** The file or directory that changed */
  changedHandle: FileSystemHandle;
  /** Path components relative to the root */
  relativePathComponents: string[];
  /** Type of change that occurred */
  type: 'appeared' | 'disappeared' | 'modified' | 'moved' | 'unknown' | 'errored';
  /** Previous location for moved items */
  relativePathMovedFrom?: string[];
}

/**
 * Callback function invoked when file system changes are detected
 */
interface FileSystemObserverCallback {
  (records: FileSystemChangeRecord[], observer: FileSystemObserver): void;
}

/**
 * FileSystemObserver API for detecting file system changes
 * @experimental Available in Chrome 129+ via origin trial or #file-system-observer flag
 */
declare class FileSystemObserver {
  constructor(callback: FileSystemObserverCallback);
  observe(handle: FileSystemHandle, options?: { recursive?: boolean }): Promise<void>;
  unobserve(handle: FileSystemHandle): void;
  disconnect(): void;
}

export interface FileEntry {
  name: string;
  path: string;
  handle: FileSystemFileHandle;
  kind: 'file';
}

export interface DirectoryEntry {
  name: string;
  path: string;
  handle: FileSystemDirectoryHandle;
  kind: 'directory';
}

export type FileSystemEntry = FileEntry | DirectoryEntry;

export type FileSystemChangeCallback = (changes: FileSystemChangeRecord[]) => void;

export class FileSystemManager {
  private rootHandle: FileSystemDirectoryHandle | null = null;
  private rootPath: string = '';
  private fileCache: Map<string, FileSystemEntry> = new Map();
  private observer: FileSystemObserver | null = null;
  private changeCallback: FileSystemChangeCallback | null = null;

  /**
   * Check if File System Access API is supported
   */
  isSupported(): boolean {
    return 'showDirectoryPicker' in window;
  }

  /**
   * Check if FileSystemObserver API is supported
   */
  isObserverSupported(): boolean {
    return 'FileSystemObserver' in self;
  }

  /**
   * Check if currently observing file system changes
   */
  isObserving(): boolean {
    return this.observer !== null;
  }

  /**
   * Set a callback to be notified of file system changes
   */
  setChangeCallback(callback: FileSystemChangeCallback | null): void {
    this.changeCallback = callback;
  }

  /**
   * Request user to select a directory.
   * Returns the handle — caller is responsible for workspace persistence.
   */
  async selectDirectory(): Promise<FileSystemDirectoryHandle> {
    if (!this.isSupported()) {
      throw new Error('File System Access API is not supported in this browser');
    }

    try {
      const handle = await window.showDirectoryPicker({
        mode: 'readwrite',
      });

      this.rootHandle = handle;
      this.rootPath = handle.name;
      this.fileCache.clear();

      return handle;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw new Error('Directory selection was cancelled');
      }
      throw error;
    }
  }

  /**
   * Set the root handle directly (for restoring from a stored workspace).
   * Caller should verify permissions before calling this.
   */
  setRootHandle(handle: FileSystemDirectoryHandle): void {
    this.stopObserving();
    this.rootHandle = handle;
    this.rootPath = handle.name;
    this.fileCache.clear();
  }

  /**
   * Query the permission state for a directory handle without requesting it.
   * Returns 'granted', 'denied', or 'prompt'.
   */
  async queryHandlePermission(
    handle: FileSystemDirectoryHandle,
    mode: 'read' | 'readwrite' = 'readwrite'
  ): Promise<PermissionState> {
    return handle.queryPermission({ mode });
  }

  /**
   * Get the current root directory handle
   */
  getRootHandle(): FileSystemDirectoryHandle | null {
    return this.rootHandle;
  }

  /**
   * Get the root directory name/path
   */
  getRootPath(): string {
    return this.rootPath;
  }

  /**
   * List all files and directories in the root directory (recursive)
   */
  async listFiles(
    directoryHandle: FileSystemDirectoryHandle = this.rootHandle!,
    basePath: string = ''
  ): Promise<FileSystemEntry[]> {
    if (!this.rootHandle) {
      throw new Error('No directory selected');
    }

    const entries: FileSystemEntry[] = [];

    for await (const [name, handle] of directoryHandle.entries()) {
      const path = basePath ? `${basePath}/${name}` : name;

      if (handle.kind === 'file') {
        const entry: FileEntry = {
          name,
          path,
          handle: handle as FileSystemFileHandle,
          kind: 'file',
        };
        entries.push(entry);
        this.fileCache.set(path, entry);
      } else if (handle.kind === 'directory') {
        const entry: DirectoryEntry = {
          name,
          path,
          handle: handle as FileSystemDirectoryHandle,
          kind: 'directory',
        };
        entries.push(entry);
        this.fileCache.set(path, entry);

        // Recursively list subdirectory contents
        const subEntries = await this.listFiles(handle as FileSystemDirectoryHandle, path);
        entries.push(...subEntries);
      }
    }

    return entries;
  }

  /**
   * Read file contents as text.
   */
  async readFile(path: string): Promise<string> {
    const entry = this.fileCache.get(path);
    if (!entry || entry.kind !== 'file') {
      throw new Error(`File not found: ${path}`);
    }

    const file = await entry.handle.getFile();
    return await file.text();
  }

  /**
   * Read file contents as binary (ArrayBuffer).
   * Use this for images, compressed files, or any non-text data.
   */
  async readFileBinary(path: string): Promise<ArrayBuffer> {
    const entry = this.fileCache.get(path);
    if (!entry || entry.kind !== 'file') {
      throw new Error(`File not found: ${path}`);
    }

    const file = await entry.handle.getFile();
    return await file.arrayBuffer();
  }

  /**
   * Write content to a file. Accepts text strings or binary data.
   */
  async writeFile(path: string, content: string | ArrayBuffer | Uint8Array): Promise<void> {
    const entry = this.fileCache.get(path);
    if (!entry || entry.kind !== 'file') {
      throw new Error(`File not found: ${path}`);
    }

    const writable = await entry.handle.createWritable();
    // Convert Uint8Array to ArrayBuffer for the File System Access API
    if (content instanceof Uint8Array) {
      await writable.write(content.buffer.slice(
        content.byteOffset,
        content.byteOffset + content.byteLength
      ) as ArrayBuffer);
    } else {
      await writable.write(content);
    }
    await writable.close();
  }

  /**
   * Create a new file
   * Note: Both the created file and any parent directories are added to the cache.
   */
  async createFile(path: string, content: string = ''): Promise<FileEntry> {
    if (!this.rootHandle) {
      throw new Error('No directory selected');
    }

    const pathParts = path.split('/');
    const fileName = pathParts.pop()!;

    let dirHandle = this.rootHandle;
    let currentPath = '';

    // Navigate to the directory (create if needed) and cache any created directories
    for (const part of pathParts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      dirHandle = await dirHandle.getDirectoryHandle(part, { create: true });

      // Cache the directory if not already cached
      if (!this.fileCache.has(currentPath)) {
        const dirEntry: DirectoryEntry = {
          name: part,
          path: currentPath,
          handle: dirHandle,
          kind: 'directory',
        };
        this.fileCache.set(currentPath, dirEntry);
      }
    }

    const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });

    // Write initial content
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();

    const entry: FileEntry = {
      name: fileName,
      path,
      handle: fileHandle,
      kind: 'file',
    };

    this.fileCache.set(path, entry);
    return entry;
  }

  /**
   * Delete a file
   */
  async deleteFile(path: string): Promise<void> {
    if (!this.rootHandle) {
      throw new Error('No directory selected');
    }

    const pathParts = path.split('/');
    const fileName = pathParts.pop()!;
    const dirPath = pathParts.join('/');

    let dirHandle = this.rootHandle;

    // Navigate to the directory
    if (dirPath) {
      for (const part of pathParts) {
        dirHandle = await dirHandle.getDirectoryHandle(part);
      }
    }

    await dirHandle.removeEntry(fileName);
    this.fileCache.delete(path);
  }

  /**
   * Rename/move a file
   * Uses a safe approach: create new file, then delete old file
   * If deletion fails, cleans up the new file to prevent duplicates
   */
  async renameFile(oldPath: string, newPath: string): Promise<void> {
    // Read the file content first
    const content = await this.readFile(oldPath);

    // Create new file with the content
    await this.createFile(newPath, content);

    // Try to delete the old file
    try {
      await this.deleteFile(oldPath);
    } catch (deleteError) {
      // If deletion fails, clean up the newly created file to prevent duplicates
      try {
        await this.deleteFile(newPath);
      } catch {
        // Ignore cleanup errors
      }
      throw new Error(
        `Failed to complete rename: could not delete original file "${oldPath}". ` +
          `Error: ${(deleteError as Error).message}`
      );
    }
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(path: string): Promise<{
    name: string;
    size: number;
    lastModified: number;
    type: string;
  }> {
    const entry = this.fileCache.get(path);
    if (!entry || entry.kind !== 'file') {
      throw new Error(`File not found: ${path}`);
    }

    const file = await entry.handle.getFile();
    return {
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      type: file.type,
    };
  }

  /**
   * Check if we have permission to access the directory
   */
  async verifyPermission(mode: 'read' | 'readwrite' = 'readwrite'): Promise<boolean> {
    if (!this.rootHandle) {
      return false;
    }

    const options: FileSystemHandlePermissionDescriptor = { mode };

    // Check if permission was already granted
    if ((await this.rootHandle.queryPermission(options)) === 'granted') {
      return true;
    }

    // Request permission
    if ((await this.rootHandle.requestPermission(options)) === 'granted') {
      return true;
    }

    return false;
  }

  /**
   * Get a file handle by path
   */
  getFileHandle(path: string): FileSystemFileHandle | null {
    const entry = this.fileCache.get(path);
    if (!entry || entry.kind !== 'file') {
      return null;
    }
    return entry.handle;
  }

  /**
   * Create a directory (and any parent directories if needed)
   */
  async createDirectory(path: string): Promise<DirectoryEntry> {
    if (!this.rootHandle) {
      throw new Error('No directory selected');
    }

    // Validate path is not empty or whitespace-only
    const trimmedPath = path.trim();
    if (!trimmedPath) {
      throw new Error('Invalid directory path: path cannot be empty or whitespace-only');
    }

    const pathParts = trimmedPath.split('/').filter((part) => part.length > 0);
    if (pathParts.length === 0) {
      throw new Error('Invalid directory path: path contains only slashes');
    }

    // Check if a file already exists at any path segment
    let checkPath = '';
    for (const part of pathParts) {
      checkPath = checkPath ? `${checkPath}/${part}` : part;
      const existingEntry = this.fileCache.get(checkPath);
      if (existingEntry && existingEntry.kind === 'file') {
        throw new Error(`Cannot create directory: a file already exists at "${checkPath}"`);
      }
    }

    let dirHandle = this.rootHandle;
    let currentPath = '';

    for (const part of pathParts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      dirHandle = await dirHandle.getDirectoryHandle(part, { create: true });

      // Cache the directory
      if (!this.fileCache.has(currentPath)) {
        const dirEntry: DirectoryEntry = {
          name: part,
          path: currentPath,
          handle: dirHandle,
          kind: 'directory',
        };
        this.fileCache.set(currentPath, dirEntry);
      }
    }

    const entry = this.fileCache.get(trimmedPath) as DirectoryEntry;
    return entry;
  }

  /**
   * Copy a file to a new location
   */
  async copyFile(sourcePath: string, destinationPath: string): Promise<FileEntry> {
    // Read the source file content
    const content = await this.readFile(sourcePath);

    // Create the destination file with the same content
    const newEntry = await this.createFile(destinationPath, content);

    return newEntry;
  }

  /**
   * Check if a path exists (file or directory)
   */
  exists(path: string): boolean {
    return this.fileCache.has(path);
  }

  /**
   * Check if a path is a directory
   */
  isDirectory(path: string): boolean {
    const entry = this.fileCache.get(path);
    return entry?.kind === 'directory';
  }

  /**
   * Check if a path is a file
   */
  isFile(path: string): boolean {
    const entry = this.fileCache.get(path);
    return entry?.kind === 'file';
  }

  /**
   * Get all entries (files and directories) - useful for tree view
   */
  getAllEntries(): FileSystemEntry[] {
    return Array.from(this.fileCache.values());
  }

  /**
   * Start observing the root directory for changes
   */
  async startObserving(): Promise<boolean> {
    if (!this.rootHandle) {
      console.warn('Cannot start observing: no root directory selected');
      return false;
    }

    if (!this.isObserverSupported()) {
      console.info('FileSystemObserver is not supported in this browser');
      return false;
    }

    try {
      // Stop any existing observer
      this.stopObserving();

      // Create new observer
      this.observer = new FileSystemObserver((records) => {
        this.handleFileSystemChanges(records);
      });

      // Start observing the root directory recursively
      await this.observer.observe(this.rootHandle, { recursive: true });
      console.info('FileSystemObserver started for:', this.rootPath);
      return true;
    } catch (error) {
      console.error('Failed to start FileSystemObserver:', error);
      return false;
    }
  }

  /**
   * Stop observing file system changes
   */
  stopObserving(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      console.info('FileSystemObserver stopped');
    }
  }

  /**
   * Handle file system change events
   */
  private handleFileSystemChanges(records: FileSystemChangeRecord[]): void {
    console.log('File system changes detected:', records);

    // Notify callback if set
    if (this.changeCallback) {
      this.changeCallback(records);
    }

    // Update cache based on change types
    for (const record of records) {
      const path = record.relativePathComponents.join('/');

      switch (record.type) {
        case 'disappeared':
          // Remove from cache
          this.fileCache.delete(path);
          break;

        case 'appeared':
        case 'modified':
          // Cache will be updated on next listFiles() call
          // or we could proactively update it here
          break;

        case 'moved':
          // Handle file/directory move
          if (record.relativePathMovedFrom && record.relativePathComponents.length > 0) {
            const oldPath = record.relativePathMovedFrom.join('/');
            const oldEntry = this.fileCache.get(oldPath);

            // Remove old entry
            this.fileCache.delete(oldPath);

            // Update entry with new path if we have it cached
            if (oldEntry) {
              // Safe to use ! because we checked length > 0 above
              const newName = record.relativePathComponents[record.relativePathComponents.length - 1]!;
              if (oldEntry.kind === 'file') {
                const newEntry: FileEntry = {
                  ...oldEntry,
                  path,
                  name: newName,
                };
                this.fileCache.set(path, newEntry);
              } else {
                const newEntry: DirectoryEntry = {
                  ...oldEntry,
                  path,
                  name: newName,
                };
                this.fileCache.set(path, newEntry);
              }
            }
          }
          break;

        case 'unknown':
        case 'errored':
          // Clear cache and let it be rebuilt
          console.warn('FileSystemObserver encountered unknown/error event, clearing cache');
          this.fileCache.clear();
          break;
      }
    }
  }

  /**
   * Clear the cache and reset in-memory state.
   * Does not modify storage — caller handles workspace cleanup.
   */
  reset(): void {
    this.stopObserving();
    this.changeCallback = null;
    this.rootHandle = null;
    this.rootPath = '';
    this.fileCache.clear();
  }
}

// Export a singleton instance
export const fileSystemManager = new FileSystemManager();
